import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon } from '@/components';
import { useSidebar } from '@/components/ui/sidebar';
import { cn } from '@/utils/styles';
import { useOfferMeeting } from '@/features/offers/calendar/hooks';
import { OfferDetails } from '@/features/offers/types';
import { EoiActionModal } from './eoi-action-modal';

type ModalType = 'accept' | 'reject' | 'meeting' | 'info' | null;

type OfferFloatingBannerProps = {
  readonly offer: OfferDetails;
  readonly mode: 'seller' | 'buyer';
};

type BannerConfig = {
  visible: boolean;
  variant: 'action' | 'info';
  color: 'green' | 'yellow' | 'red' | 'neutral';
  icon: string;
  label: string;
  buttonLabel: string;
  buttonColor: string;
  modalType: ModalType;
};

const COLORS = {
  green: { border: 'border-green-200', bg: 'bg-green-50', icon: 'text-green-700', text: 'text-green-900' },
  yellow: { border: 'border-amber-200', bg: 'bg-amber-50', icon: 'text-amber-700', text: 'text-amber-900' },
  red: { border: 'border-red-200', bg: 'bg-red-50', icon: 'text-red-700', text: 'text-red-900' },
  neutral: { border: 'border-neutral-100', bg: 'bg-white', icon: 'text-neutral-700', text: 'text-neutral-900' },
};

export function OfferFloatingBanner({ offer, mode }: OfferFloatingBannerProps) {
  const { t } = useTranslation(['offers']);
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';

  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const { isScheduled, hasSlots } = useOfferMeeting(offer.id);

  const status = offer.status.name as 'pending' | 'accepted' | 'rejected';

  const config = getBannerConfig({ status, mode, isScheduled, hasSlots, t });

  if (!config.visible) return null;

  const colors = COLORS[config.color];

  return (
    <>
      <div
        className={cn(
          'fixed right-0 bottom-0 z-5 border-t px-4 py-3 shadow-lg lg:hidden',
          'left-0',
          isCollapsed ? 'md:left-[60px]' : 'md:left-[220px]',
          colors.border,
          colors.bg
        )}
        style={{ transition: 'left 150ms ease' }}
      >
        {config.variant === 'action' ? (
          <div className="flex gap-3">
            <Button size="md" color="primary" className="flex-1" onClick={() => setActiveModal('accept')}>
              {t('Accept EOI')}
            </Button>
            <Button size="md" color="secondary" className="flex-1" onClick={() => setActiveModal('reject')}>
              {t('Decline EOI')}
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Icon name={config.icon} className={cn('h-5 w-5 shrink-0', colors.icon)} />
              <p className={cn('text-sm font-medium', colors.text)}>{config.label}</p>
            </div>
            <Button
              size="sm"
              color={config.buttonColor}
              className="shrink-0"
              onClick={() => setActiveModal(config.modalType)}
            >
              {config.buttonLabel}
            </Button>
          </div>
        )}
      </div>

      {/* Modal rendered by the banner */}
      <EoiActionModal type={activeModal} onClose={() => setActiveModal(null)} offer={offer} mode={mode} />
    </>
  );
}

function getBannerConfig({
  status,
  mode,
  isScheduled,
  hasSlots,
  t,
}: {
  status: 'pending' | 'accepted' | 'rejected';
  mode: 'seller' | 'buyer';
  isScheduled: boolean;
  hasSlots: boolean;
  t: (key: string) => string;
}): BannerConfig {
  // Meeting confirmed (both roles)
  if (status === 'accepted' && isScheduled) {
    return {
      visible: true,
      variant: 'info',
      color: 'green',
      icon: 'check',
      label: t('Meeting confirmed!'),
      buttonLabel: t('View details'),
      buttonColor: 'primary',
      modalType: 'meeting',
    };
  }

  // Rejected (both roles)
  if (status === 'rejected') {
    return {
      visible: true,
      variant: 'info',
      color: 'red',
      icon: 'x',
      label: t('EOI declined'),
      buttonLabel: t('View details'),
      buttonColor: 'secondary',
      modalType: 'info',
    };
  }

  // Seller states
  if (mode === 'seller') {
    if (status === 'pending') {
      return {
        visible: true,
        variant: 'action',
        color: 'neutral',
        icon: '',
        label: '',
        buttonLabel: '',
        buttonColor: 'ghostPrimary',
        modalType: null,
      };
    }

    if (status === 'accepted' && hasSlots) {
      return {
        visible: true,
        variant: 'info',
        color: 'green',
        icon: 'clock',
        label: t('Waiting for buyer to confirm'),
        buttonLabel: t('View slots'),
        buttonColor: 'primary',
        modalType: 'meeting',
      };
    }
  }

  // Buyer states
  if (mode === 'buyer') {
    if (status === 'pending') {
      return {
        visible: true,
        variant: 'info',
        color: 'yellow',
        icon: 'clock',
        label: t('Your EOI is under review'),
        buttonLabel: t('View status'),
        buttonColor: 'ghost',
        modalType: 'info',
      };
    }

    if (status === 'accepted' && hasSlots && !isScheduled) {
      return {
        visible: true,
        variant: 'info',
        color: 'green',
        icon: 'calendar',
        label: t('Your EOI has been accepted!'),
        buttonLabel: t('Select meeting slot'),
        buttonColor: 'primary',
        modalType: 'meeting',
      };
    }
  }

  return {
    visible: false,
    variant: 'info',
    color: 'neutral',
    icon: '',
    label: '',
    buttonLabel: '',
    buttonColor: 'primary',
    modalType: null,
  };
}
